import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Pagination } from 'antd';
import { Button, LinkButton } from "../Button";
import { OvalLoading } from "../Screens";
import { SearchComponent } from "./Search";
import { SearchOutlined, FilterFilled } from '@ant-design/icons';
import { TextHighlighter } from '@neumetric/blueprint-lib/components';
import "./style.scss"

export const ROW_EXPAND_POLICY = {
  "ROW_CLICK": "ROW_CLICK",
  "ALWAYS": "ALWAYS"
}

const EmptyData = (props) => {
  const { height, message } = props
  return (
    <div className='col h-ctr v-ctr' style={{ height }}>
      <h6 className='exo2 f8 med c00085'>{message || 'No Data'}</h6>
    </div>
  )
}
const CustomHighlighter = (props) => {
  const { searchText, text, ...rest } = props
  return (
    <TextHighlighter
      highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
      searchWords={[searchText]}
      autoEscape
      textToHighlight={text ? text.toString() : ''}
      {...rest}
    />
  )
}
const DocumentTitle = (col, text, record, index) => {
  return (
    <>
      <Link to={`${record.link || (record.guid || record.id)}`} className={`${col.cellClassName || 'f9'} line-22 c1133CC`}>
        {
          col.searchedColumn === col.dataIndex ?
            <CustomHighlighter
              searchText={col.searchText}
              text={text}
            />
            :
            text
        }
      </Link>
    </>
  )
}
const HTMLCell = (col, text, record) => {
  const content = (text && typeof text === "object" && text.label) ? text.label : text
  return (
    col.searchedColumn === col.dataIndex ?
      <CustomHighlighter searchText={col.searchText} text={content} />
      :
      <p className={`${col.cellClassName} ck-container default`} dangerouslySetInnerHTML={{ __html: content }} ></p>
  )
}
const Cell = (col, text, record, index) => {
  const content = (text && typeof text === "object" && text.label) ? text.label : text;
  return (
    <span className={`${col.cellClassName || 'f9'} c00 line-22`} >
      {
        col.searchedColumn === col.dataIndex ?
          <CustomHighlighter searchText={col.searchText} text={content} />
          :
          content
      }
    </span>
  )
}
const ButtonCell = (col, text, record, index) => {
  return <Button label={text} variant={col.variant} color={col.color} onClick={() => col.onClick && col.onClick(record)} />
}
const ColorCell = (col, text, record, index) => {
  const content = (text && typeof text === "object" && text.label) ? text.label : text;
  return (
    <div className='row cell'>
      <div className={`cel-circle bg${content}`}></div>
      <span className={`${col.cellClassName || 'f9'} c000 line-22 caps`}>
        {
          col.searchedColumn === col.dataIndex ?
            <CustomHighlighter searchText={col.searchText} text={content} />
            :
            content
        }
      </span>
    </div>
  )
}
const ActionsCell = (col, text, record, index) => {
  const { actions, getActions } = col;
  let buttons = actions || (typeof getActions === 'function' ? getActions(record) : []);
  return (
    buttons.length > 0 ?
      <div className='row v-ctr h-ctr'>
        {
          buttons.map((_) => {
            return Boolean(_.link) || Boolean(_.href) ? <LinkButton {..._} /> : <Button {..._} />
          })
        }
      </div>
      : null
  )
}
const Sorter = (dataIndex, recordA, recordB) => {
  var nameA = recordA[dataIndex], nameB = recordB[dataIndex];
  nameA = ((typeof nameA === 'object' ? nameA.label : nameA) || '').toLowerCase();
  nameB = ((typeof nameB === 'object' ? nameB.label : nameB) || '').toLowerCase();
  if (nameA < nameB) {
    return -1;
  } else if (nameA > nameB) {
    return 1;
  }
  return 0;
}

const DefaultColumns = [
  { title: 'Version', dataIndex: 'version', key: 'version', width: '5%', render: Cell },
  { title: 'ID', dataIndex: 'id', key: 'id', width: '10%', render: Cell },
  { title: 'Document', dataIndex: 'title', key: 'title', width: '35%', render: DocumentTitle, search: true },
  { title: 'Department', dataIndex: 'department', key: 'department', width: '10%', render: Cell, search: true },
  { title: 'Status', dataIndex: 'status', key: 'status', width: '10%', render: ColorCell, sorter: Sorter.bind(null, 'status') },
  { title: 'Clasification', dataIndex: 'clasification', key: 'clasification', width: '10%', render: ColorCell },
  { title: 'Type', dataIndex: 'type', key: 'type', width: '10%', render: Cell, sorter: Sorter.bind(null, 'type') },
  { title: 'Last Updated', dataIndex: 'updated_at', key: 'updated_at', width: '10%', render: Cell },
]

export const DocumentTable = (props) => {
  const { rows, onSelectionChange, EmptyComponent, emptyProps, Columns, noSelection, scrollOffset, totalCount, onRowExpandRender, pageSize } = props;
  const expandPolicy = props.expandPolicy || ROW_EXPAND_POLICY.ROW_CLICK
  const [state, setState] = useState({ searchedColumn: null, searchText: '', height: 100, showfilterColumn: '', expandedKeys: [] })
  const searchInput = useRef(null)
  const tableRef = useRef(null)
  const handleServerSearch = (col, searchText) => {
    if (col.supportServerSearch) {
      props.onSearch && props.onSearch(col.dataIndex, searchText)
    }
  }
  const onTableRowExpand = (expanded, record) => {
    const expandedKeys = [...state.expandedKeys]
    if (expanded) {
      expandedKeys.push(record.key);
    } else {
      let index = expandedKeys.indexOf(record.key)
      expandedKeys.splice(index, 1)
    }
    setState((_) => ({ ..._, expandedKeys }))
  }
  const getExpandableProps = () => {
    let config;
    if ((typeof onRowExpandRender) !== 'undefined') {
      config = {
        expandRowByClick: true,
        showExpandColumn: false,
        expandedRowRender: onRowExpandRender,
        expandedRowKeys: state.expandedKeys
      }
      if (expandPolicy === ROW_EXPAND_POLICY.ROW_CLICK) {
        config.onExpand = onTableRowExpand
      }
    }
    return config
  }
  const getFilterOptions = (column) => {
    return ({
      filterMultiple: false,
      filterDropdownOpen: state.showfilterColumn === column.dataIndex,
      filterDropdown: (({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        return (
          <SearchComponent
            {...column}
            value={selectedKeys[0]}
            searchInput={searchInput}
            onSearchChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }}
            onFilter={() => {
              confirm();
              handleServerSearch(column, selectedKeys[0]);
              setState((_) => ({ ..._, searchText: selectedKeys[0], searchedColumn: column.dataIndex }))
            }}
            onReset={() => {
              setState((_) => ({ ..._, searchText: '' }))
              clearFilters();
            }}
            onClear={() => {
              handleServerSearch(column, '');
              setState((_) => ({ ..._, searchText: '', searchedColumn: null }))
              clearFilters();
              confirm();
            }}
            onSearch={() => {
              confirm({ closeDropdown: false });
              handleServerSearch(column, selectedKeys[0]);
              setState((_) => ({ ..._, searchText: selectedKeys[0], searchedColumn: column.dataIndex }))
            }}
          />
        )
      }),
      filterIcon: (filtered) => (
        <SearchOutlined style={filtered ? { color: '#1890ff' } : {}} />
      ),
      onFilter: (value, record) => {
        let attributeVal = record[column.dataIndex];
        attributeVal = (attributeVal && typeof attributeVal === "object") ? attributeVal.label : (attributeVal || '');
        return attributeVal.toString().toLowerCase().includes(value.toLowerCase())
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setState((_) => ({ ..._, showfilterColumn: column.dataIndex }))
          setTimeout(() => searchInput.current && searchInput.current?.focus(), 100);
        } else {
          setState((_) => ({ ..._, showfilterColumn: '' }))
        }
      }
    })
  }
  const getColumns = () => {
    const columns = Columns || DefaultColumns;
    return columns.map((_) => {
      let col = { ..._ }
      col.searchedColumn = state.searchedColumn;
      col.searchText = state.searchText
      if (col.render) {
        if (typeof col.render === "string") {
          switch (col.render) {
            case 'color': col.render = ColorCell.bind(null, col); break;
            case 'link': col.render = DocumentTitle.bind(null, col); break;
            case 'btn': col.render = ButtonCell.bind(null, col); break;
            case 'html': col.render = HTMLCell.bind(null, col); break;
            case 'actions': col.render = ActionsCell.bind(null, col); break;
            default: col.render = Cell.bind(null, col);
          }
        }
      } else {
        col.render = Cell.bind(null, col)
      }
      if (col.sort) {
        col.sorter = Sorter.bind(null, col.dataIndex);
      }
      if (col.search) {
        col = { ...col, ...getFilterOptions(col) }
      }
      return col;
    })
  }
  useEffect(() => {
    if (tableRef && tableRef.current && Array.isArray(rows)) {
      let height = tableRef.current.clientHeight, expandedKeys = [];
      if ((height) < 400) {
        height = rows.length === 0 ? 300 : 360
      }
      if (expandPolicy === ROW_EXPAND_POLICY.ALWAYS) {
        expandedKeys = rows.map(({ key }) => key);
      }
      setState((_) => ({ ..._, height: height, expandedKeys }))
    }
  }, [rows]);
  const customItemRender = (page, type, originalElement) => {
    if (type === 'jump-next' || type === 'jump-prev') {
      return originalElement
    }

    return (
      <div className='col page-item v-ctr h-ctr' title={page}>
        {
          (type === 'page') ?
            <React.Fragment><span className={`exo2 f14 page-no`}>{page}</span></React.Fragment>
            :
            originalElement
        }
      </div>
    )
  };
  return (
    <div className={`col w-100 table-ct o-hide ${props.height || 'h-100'}`} ref={tableRef}>
      {
        state.height &&
        <div className='col f-rest'>
          <Table
            className='doc-table'
            sticky
            locale={{
              emptyText: (
                rows === null ?
                  <OvalLoading isSmall />
                  : !Boolean(state.searchedColumn) && Boolean(EmptyComponent) ?
                    <EmptyComponent />
                    :
                    <EmptyData message={Boolean(state.searchedColumn) ? "No Matching Data" : props.emptyMessage} height={emptyProps && emptyProps.height ? emptyProps.height : state.height - 140} />
              )
            }}
            rowSelection={noSelection ? undefined : {
              type: 'checkbox',
              onChange: (selectedRowKeys, selectedRows) => {
                onSelectionChange && onSelectionChange(selectedRows)
              }
            }}
            scroll={{
              y: (state.height - (scrollOffset || 100)),
            }}
            pagination={false}
            columns={getColumns()}
            dataSource={rows || []}
            expandable={getExpandableProps()} />
        </div>
      }
      {
        totalCount > 0 &&
        <div className={`col v-ctr h-ctr page-change ${props.paginationClassName}`}>
          <Pagination
            defaultCurrent={1}
            pageSize={pageSize || 10}
            showSizeChanger={false}
            total={totalCount}
            onChange={props.onPageChange}
            itemRender={customItemRender}
          />
        </div>
      }
    </div>
  )
}